import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, firstValueFrom, map, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AbstractDocumentService } from '../../abstracts/services/common/document.abstract';
import { IApiResponse } from '../../interfaces/api/api-response.interface';
import { IDocumentType } from '../../interfaces/common/document/document-type.interface';
import { IDocument } from '../../interfaces/common/document/document.interface';
import { IAppState } from '../../store/common/interfaces/app-state.interface';
import {
  applyEnrollmentIdToQueryParams,
  applyUserProfileId,
} from '../../ui-helpers/functions/user-context-url.func';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentService extends AbstractDocumentService {
  private apiUrl = environment.apiUrl;
  private controllerSuffix = 'OnBoarding/OnBoarding/';

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    private store: Store<IAppState>,
  ) {
    super();
  }

  async getDocumentTypes(): Promise<IDocumentType[]> {
    const url = `${this.apiUrl}${this.controllerSuffix}document-types`;
    const result = await firstValueFrom(
      this.http
        .get<IApiResponse>(url)
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    return result.data as IDocumentType[];
  }

  async getCreditDocumentTypes(): Promise<IDocumentType[]> {
    const url = `${this.apiUrl}${this.controllerSuffix}credit-document-types`;
    const result = await firstValueFrom(
      this.http
        .get<IApiResponse>(url)
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    return result.data as IDocumentType[];
  }

  async getInvoiceDocumentTypes(): Promise<IDocumentType[]> {
    const url = `${this.apiUrl}${this.controllerSuffix}invoice-document-types`;
    const result = await firstValueFrom(
      this.http
        .get<IApiResponse>(url)
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    return result.data as IDocumentType[];
  }

  async deleteDocumentById(id: number): Promise<IApiResponse> {
    const url = `${this.apiUrl}${this.controllerSuffix}delete-document/${id}`;
    const result = await firstValueFrom(
      this.http
        .get<IApiResponse>(url)
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    return result;
  }

  async downloadEnrollmentDocument(id: number): Promise<IDocument> {
    const url = `${this.apiUrl}${this.controllerSuffix}download-document/${id}`;
    const result = await firstValueFrom(
      this.http
        .get<IApiResponse>(url)
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    return result.data as IDocument;
  }

  async deleteDocumentByParams(queryParams: string): Promise<IApiResponse> {
    const url = `${this.apiUrl}${this.controllerSuffix}delete-document${queryParams}`;
    const result = await firstValueFrom(
      this.http
        .get<IApiResponse>(url)
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    return result;
  }

  async uploadEnrollmentDocument(model: {
    files: File[];
    documentTypeId: number;
  }): Promise<IApiResponse> {
    // Form Data
    const formData: any = new FormData();
    if (model.files.length > 0) {
      for (let i = 0; i < model.files.length; i += 1) {
        formData.append('File', model.files[i]);
      }
    }
    formData.append('DocumentTypeId', model.documentTypeId);

    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    params = applyUserProfileId(params, this.store);
    const url = `${this.apiUrl}${this.controllerSuffix}upload-enrollment-document`;
    const result = await firstValueFrom(
      this.http
        .post<IApiResponse>(url, formData, { params })
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    return result;
  }

  async getEnrollmentDocumentsById(): Promise<IDocument[]> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.controllerSuffix}enrollment-documents`;
    return firstValueFrom(
      this.http.get<IApiResponse>(url, { params }).pipe(
        map((response) => response.data as IDocument[]),
        catchError((error: ErrorEvent) =>
          throwError(() => new Error(error.message)),
        ),
      ),
    );
  }

  async getCustomerCreditDocumentsByClientCustomerId(
    clientCustomerId: number,
  ): Promise<IDocument[]> {
    const url = `${this.apiUrl}${this.controllerSuffix}customer-credit-documents/${clientCustomerId}`;
    const result = await firstValueFrom(
      this.http.get<IApiResponse>(url).pipe(
        map((response) => response.data as IDocument[]),
        catchError((error: ErrorEvent) =>
          throwError(() => new Error(error.message)),
        ),
      ),
    );
    console.log('result, ', result);
    return result;
  }

  async downloadCreditRequestDocument(docId: number): Promise<IDocument> {
    const url = `${this.apiUrl}${this.controllerSuffix}download-credit-request-document/${docId}`;
    const result = await firstValueFrom(
      this.http
        .get<IApiResponse>(url)
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    return result.data as IDocument;
  }

  async getMsaUrl(): Promise<string> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.controllerSuffix}msa-url`;
    const response = await firstValueFrom(
      this.http.get<IApiResponse>(url, { params }),
    );
    return response.data as string;
  }
}
